import type { LocationData } from '@/lib/location-types'

const SAVED_INPUTS_KEY = 'lawn-care-saved-inputs'

// Keep `lawn-care-saved-inputs.zip` (used by useSavedInputs across tool
// pages) in sync with the canonical `location-override` ZIP so the two
// stores never drift.
function syncSavedInputsZip(zip: string | null): void {
  if (typeof window === 'undefined') return
  try {
    const raw = localStorage.getItem(SAVED_INPUTS_KEY)
    const current = raw ? JSON.parse(raw) : {}
    if (zip) {
      current.zip = zip
    } else {
      delete current.zip
    }
    localStorage.setItem(SAVED_INPUTS_KEY, JSON.stringify(current))
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Get manual location override from localStorage.
 */
export function getLocationOverride(): LocationData | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem('location-override')
    if (!stored) return null

    const data = JSON.parse(stored) as LocationData

    if (!data.zip || !data.lat || !data.lon) return null
    return { ...data, source: 'override' }
  } catch {
    return null
  }
}

/**
 * Geocode a ZIP code via server API so zipcodes DB stays server-only.
 */
export async function geocodeZip(zipCode: string): Promise<LocationData | null> {
  if (!zipCode || zipCode.length !== 5 || !/^\d{5}$/.test(zipCode)) return null

  try {
    const response = await fetch(`/api/location/zip?zip=${zipCode}`)
    if (!response.ok) return null

    const data = await response.json()
    return data.location || null
  } catch {
    return null
  }
}

/**
 * Save manual location override to localStorage.
 */
export async function setLocationOverride(zipCode: string): Promise<boolean> {
  if (typeof window === 'undefined') return false

  const location = await geocodeZip(zipCode)
  if (!location) return false

  try {
    localStorage.setItem(
      'location-override',
      JSON.stringify({
        ...location,
        source: 'override',
      })
    )
    syncSavedInputsZip(location.zip)
    return true
  } catch {
    return false
  }
}

/**
 * Validate ZIP code format and existence via server API.
 */
export async function isValidZip(zip: string): Promise<boolean> {
  if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) return false
  const location = await geocodeZip(zip)
  return !!location
}

/**
 * Clear manual location override.
 */
export function clearLocationOverride(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem('location-override')
  syncSavedInputsZip(null)
}

/**
 * Get location display string.
 */
export function getLocationDisplayName(location: LocationData): string {
  if (location.city && location.state) {
    return `${location.city}, ${location.state} ${location.zip}`
  }
  return `ZIP ${location.zip}`
}

/**
 * Get location source label for UI.
 */
export function getLocationSourceLabel(source: LocationData['source']): string {
  switch (source) {
    case 'override':
      return 'Manual Location'
    case 'lawn_stored':
      return 'Lawn Profile'
    case 'lawn_geocoded':
      return 'Lawn ZIP Code'
    case 'fallback':
      return 'Default Location'
  }
}
